import { observable, computed, action } from 'mobx'
import { has, isArray, isObject } from 'lodash'
import { IDMMFFieldDefault } from '@prisma/studio-types'

import BaseListItem, {
  IBaseListItemUpdateOptions,
} from 'stores/base/BaseListItem'
import ModelStore from 'stores/model/ModelStore'
import Model from 'stores/model/Model'
import EnumStore from 'stores/enum/EnumStore'
import Enum from 'stores/enum/Enum'

import createError from 'utils/createError'

export interface IFieldCreatePayload {
  id: string
  modelId: string
  name: string
  type: string
  kind: string
  default: IDMMFFieldDefault
  isId: boolean
  isUnique: boolean
  isRequired: boolean
  isList: boolean
  isReadOnly: boolean
  isUpdatedAt: boolean
  relationName: string
  relationFromFieldNames: string[]
  relationToFieldNames: string[]
}

class Field extends BaseListItem {
  @observable id: IFieldCreatePayload['id']
  @observable modelId: IFieldCreatePayload['modelId']
  @observable name: IFieldCreatePayload['name']
  @observable type: IFieldCreatePayload['type']
  @observable kind: IFieldCreatePayload['kind']
  @observable default: IFieldCreatePayload['default']
  @observable isId: IFieldCreatePayload['isId']
  @observable isUnique: IFieldCreatePayload['isUnique']
  @observable isRequired: IFieldCreatePayload['isRequired']
  @observable isList: IFieldCreatePayload['isList']
  @observable isReadOnly: IFieldCreatePayload['isReadOnly']
  @observable isUpdatedAt: IFieldCreatePayload['isUpdatedAt']
  @observable relationName: IFieldCreatePayload['relationName']
  @observable
  relationFromFieldNames: IFieldCreatePayload['relationFromFieldNames']
  @observable relationToFieldNames: IFieldCreatePayload['relationToFieldNames']

  constructor(field: IFieldCreatePayload) {
    super(field)

    this.id = field.id
    this.modelId = field.modelId
    this.name = field.name
    this.type = field.type
    this.kind = field.kind
    if (this.type === 'Json' && typeof field.default === 'string') {
      // For JSON fields, the default is represented as a string, so we try to parse it as JSON.
      // If anything fails, just use the default from the DMMF, since we do not want to lose data at any point
      try {
        this.default = JSON.parse(field.default)
      } catch (e) {
        this.default = field.default
      }
    } else if (this.type === 'BigInt' && typeof field.default === 'string') {
      // For BigInt fields, the default is represented as a string, so we try to parse it as BigInt.
      // If anything fails, just use the default from the DMMF, since we do not want to lose data at any point
      try {
        this.default = BigInt(field.default)
      } catch (e) {
        this.default = field.default
      }
    } else {
      this.default = field.default
    }
    this.isId = field.isId
    this.isUnique = field.isUnique
    this.isRequired = field.isRequired
    this.isList = field.isList
    this.isReadOnly = field.isReadOnly
    this.isUpdatedAt = field.isUpdatedAt
    this.relationName = field.relationName
    this.relationFromFieldNames = field.relationFromFieldNames
    this.relationToFieldNames = field.relationToFieldNames
  }

  @computed
  get model(): Model | null {
    return ModelStore.get(this.modelId)
  }

  @computed
  get isScalar(): boolean {
    return this.kind === 'scalar' && !this.isEnum
  }

  @computed
  get isString(): boolean {
    return this.type === 'String'
  }

  @computed
  get isInt(): boolean {
    return this.type === 'Int'
  }

  @computed
  get isBigInt(): boolean {
    return this.type === 'BigInt'
  }

  @computed
  get isFloat(): boolean {
    return this.type === 'Float'
  }

  @computed
  get isDecimal(): boolean {
    return this.type === 'Decimal'
  }

  @computed
  get isBoolean(): boolean {
    return this.type === 'Boolean'
  }

  @computed
  get isDateTime(): boolean {
    return this.type === 'DateTime'
  }

  @computed
  get isJson(): boolean {
    return this.type === 'Json'
  }

  @computed
  get isBytes(): boolean {
    return this.type === 'Bytes'
  }

  @computed
  get isEnum(): boolean {
    return !!this.typeAsEnum
  }

  @computed
  get isRelation(): boolean {
    return this.kind === 'object'
  }

  @computed
  get isPartOfRelation(): boolean {
    return this.relationItIsPartOf !== null
  }

  @computed
  get relationItIsPartOf(): Field | null {
    if (!this.model) {
      return null
    }

    return (
      this.model.fields.find(
        f => f.isRelation && f.relationFromFieldNames.includes(this.name)
      ) || null
    )
  }

  @computed
  get isSortable(): boolean {
    return (this.isScalar && !this.isList) || (this.isEnum && !this.isList)
  }

  @computed
  get isFunctionDefault(): boolean {
    return (
      typeof this.default !== 'string' &&
      typeof this.default !== 'number' &&
      typeof this.default !== 'boolean' &&
      typeof this.default !== 'bigint' &&
      !isArray(this.default)
    )
  }

  @computed
  get defaultAsString(): string {
    if (this.isList) {
      return '[]'
    }

    if (!this.default) {
      return ''
    }

    if (
      typeof this.default === 'string' ||
      typeof this.default === 'number' ||
      typeof this.default === 'boolean'
    ) {
      return `${this.default}`
    }

    if (typeof this.default === 'bigint') {
      return this.default.toString()
    }

    if (isArray(this.default)) {
      return this.default.toString()
    }

    if (isObject(this.default)) {
      return `${this.default.name}()`
    }

    return ''
  }

  @computed
  get placeholder(): string {
    if (this.isList) {
      return '[]'
    }

    if (this.isString) {
      return 'Value'
    }

    if (this.isInt || this.isFloat || this.isBigInt || this.isDecimal) {
      return '1337'
    }

    if (this.isBoolean) {
      return 'false'
    }

    if (this.isDateTime) {
      return new Date().toISOString()
    }

    if (this.isJson) {
      return '{}'
    }

    if (this.isEnum) {
      return this.typeAsEnum!.values[0]
    }

    if (this.isRelation) {
      return 'ID'
    }

    return 'Value'
  }

  @computed
  get typeAsModel(): Model | null {
    if (!this.isRelation) {
      return null
    }

    return ModelStore.getByName(this.type)
  }

  @computed
  get typeAsEnum(): Enum | null {
    return EnumStore.getByName(this.type)
  }

  @computed
  get typeAsLabel() {
    let label = this.type

    const typeModel = this.typeAsModel
    if (typeModel) {
      label = typeModel.name
    }

    if (this.isList) {
      label += '[]'
    } else if (!this.isRequired) {
      label += '?'
    }

    return label
  }

  @computed
  get lowestValidValue(): string | number | boolean | null | {} | [] {
    if (this.isList) {
      return []
    }

    if (!this.isRequired) {
      return null
    }

    if (this.isString) {
      return ''
    }

    if (this.isInt || this.isFloat || this.isDecimal) {
      return 0
    }

    if (this.isBigInt) {
      return BigInt(0)
    }

    if (this.isBoolean) {
      return false
    }

    if (this.isDateTime) {
      return new Date(0).toISOString()
    }

    if (this.isJson) {
      return {}
    }

    if (this.isEnum) {
      if (!this.typeAsEnum) {
        throw createError(
          'Field.lowestValidValue',
          'Invalid type of field: enum',
          {
            fieldId: this.id,
            type: this.type,
          }
        )
      }
      return this.typeAsEnum.values[0]
    }

    return null
  }

  @action
  update(
    field: Partial<IFieldCreatePayload>,
    options: IBaseListItemUpdateOptions = {}
  ) {
    has(field, 'default') && (this.default = field.default!)
    has(field, 'isId') && (this.isId = field.isId!)
    has(field, 'isUnique') && (this.isUnique = field.isUnique!)
    has(field, 'isRequired') && (this.isRequired = field.isRequired!)
    has(field, 'isList') && (this.isList = field.isList!)
    has(field, 'isReadOnly') && (this.isReadOnly = field.isReadOnly!)
    has(field, 'isUpdatedAt') && (this.isUpdatedAt = field.isUpdatedAt!)

    super.update(field, options)
  }
}

export default Field
